import {
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	useMemo
} from "react";
import { ButtonBase } from "../ButtonBase/ButtonBase";
import {
	Icon,
	IconSize,
	IconValue
} from "../Icon/Icon";
import styles from "./IconAndText.module.less";
import {
	isNotNil,
	setTrackingId
} from "client/Misc/utils";

export type IconAndTextProps = {
	text: string | number;
	tooltip?: string;
	onClick?(): void;
	trackingId?: string;
};

export const IconAndText = (props: PropsWithChildren<IconAndTextProps>): ReactElement => {
	const {
		text,
		tooltip,
		onClick,
		trackingId,
		children
	} = props;

	const clickHandler = useMemo(
		(): ((args: MouseEvent) => void) | undefined => {
			if (!onClick) {
				return undefined;
			}

			return (args): void => {
				args.preventDefault();
				args.stopPropagation();
				onClick();
			};
		},
		[onClick]
	);

	const renderContent = (): ReactElement => (
		<span
			{...setTrackingId(trackingId)}
			title={tooltip}
		>
			{children}
			<span
				{...setTrackingId(trackingId, "__text")}
				className={styles.text}
			>
				{text}
			</span>
		</span>
	);

	return isNotNil(clickHandler)
		? (
			<ButtonBase
				className={styles.button}
				onClick={clickHandler}
			>
				{renderContent()}
			</ButtonBase>
		)
		: renderContent();
};

IconAndText.displayName = "IconAndText";

type IconAndTextIconProps = {
	value: IconValue;
	size?: IconSize;
	trackingId?: string;
};

const IconAndTextIcon = (props: IconAndTextIconProps): ReactElement => {
	const {
		value,
		size,
		trackingId
	} = props;

	const iconValue = useMemo(
		(): IconValue => {
			if (typeof value === "string") {
				return {
					kind: "font",
					name: value,
					color: "secondary"
				};
			}

			return value;

		},
		[value]
	);

	return (
		<span
			{...setTrackingId(trackingId)}
			className={styles.icon}
		>
			<Icon value={iconValue} size={size}/>
		</span>
	);
};

IconAndTextIcon.displayName = "IconAndText.Icon";

IconAndText.Icon = IconAndTextIcon;
